/**
 * 参数配置 - 类型定义
 *
 * 接口字段以后端最终契约为准；这里定义的是前端使用层模型，
 * 与 api/paramSettingApi.ts 中的请求体类型一一对应
 *
 * 数据模型三级：栏目 ColumnVO > 模块 ModuleVO > 参数 ParamVO
 */

/**
 * 参数来源枚举（后端存储的原始值）
 * - CUSTOM_TEXT  自定义文本（单行 / 多行）
 * - CONTACTS     通讯录（多选）
 * - APP          产品（dbtId 多选）
 * - CHANNEL      渠道（payChnl 多选）
 */
export type ParamSource = 'CUSTOM_TEXT' | 'CONTACTS' | 'APP' | 'CHANNEL'

/**
 * 参数值控件类型（前端渲染层概念，后端原样存储）
 * - TEXT          单行输入框
 * - LARGE_TEXT    多行文本域（用于 prompt 等长文本）
 * - MULTI_SELECT  下拉多选（用于枚举型来源）
 */
export type ParamValueType = 'TEXT' | 'LARGE_TEXT' | 'MULTI_SELECT'

/**
 * 来源枚举 → 中文展示（用于表格列、tag、下拉 label）
 */
export const PARAM_SOURCE_LABEL: Record<ParamSource, string> = {
  CUSTOM_TEXT: '自定义文本',
  CONTACTS: '通讯录',
  APP: '产品',
  CHANNEL: '渠道',
}

/**
 * 编辑弹窗「参数来源」下拉的 options
 * 顺序即下拉展示顺序
 */
export const PARAM_SOURCE_OPTIONS: { value: ParamSource; label: string }[] = [
  { value: 'CUSTOM_TEXT', label: '自定义文本' },
  { value: 'CONTACTS', label: '通讯录' },
  { value: 'APP', label: '产品' },
  { value: 'CHANNEL', label: '渠道' },
]

/**
 * 参数（最小粒度配置项）
 */
export interface ParamVO {
  id: number
  /** 所属模块 id */
  moduleId: number
  /** 参数名称（用户可见） */
  paramName: string
  /** 参数编码（业务侧用 key；首次保存后锁定） */
  paramCode: string
  /** 参数来源；决定 paramValue 的存储格式与渲染控件 */
  paramSource: ParamSource
  /** 控件类型；CUSTOM_TEXT 时区分 TEXT/LARGE_TEXT，枚举型固定 MULTI_SELECT */
  paramValueType?: ParamValueType
  /**
   * 后端原始存储值
   * - CUSTOM_TEXT 时为字符串
   * - 枚举型来源时为 JSON 数组字符串，解析后按对应字段拼接展示
   */
  paramValue: string
  /** 备注（用户填写） */
  remark?: string
  /** 是否已锁定编码（true 表示编辑弹窗中编码字段禁用） */
  codeLocked?: boolean
  createTime?: string
  updateTime?: string
}

/**
 * 模块（参数容器）
 */
export interface ModuleVO {
  id: number
  /** 所属栏目 id */
  columnId: number
  /** 模块名称 */
  moduleName: string
  /**
   * 模块编码（业务侧标识 key）
   * 格式约束：仅支持字母 / 数字 / 下划线，且必须以字母开头
   */
  moduleCode: string
  /** 排序位（升序展示，前端按此值排序） */
  sortOrder?: number
  /** 关联参数（接口当前不下发，前端通过单独的参数接口加载） */
  params?: ParamVO[]
}

/**
 * 栏目布局类型（前端控制，后端不存储）
 * - list: 标准纵向模块列表
 * - twoColumn: 通知中心样式（左模块导航 + 右参数配置）— 后续 M9 接入
 */
export type ColumnLayout = 'list' | 'twoColumn'

/**
 * 栏目（顶层 Tab）
 */
export interface ColumnVO {
  id: number
  /** 栏目名称（Tab 上展示） */
  columnName: string
  /** 排序位（升序展示） */
  sortOrder?: number
  createTime?: string
  /** 布局类型；前端字段，接口不返回 */
  layout?: ColumnLayout
  /** 关联模块（接口当前不下发，前端通过模块接口加载） */
  modules?: ModuleVO[]
}

// ===== 来源候选项子结构（与后端存储格式对应）=====

/** 通讯录子项：CONTACTS 来源的 JSON 数组元素 */
export interface ContactItem {
  userId: string
  userName: string
  name: string
}
/** 产品子项：APP 来源的 JSON 数组元素 */
export interface AppItem {
  dbtId: string
  appName: string
}

/**
 * 来源候选项通用结构
 * 接口 /setting/param/source/options 返回的 list 元素
 * 多选下拉的 value/label 直接复用此结构
 */
export interface SourceOption {
  value: string
  label: string
}

// ===== 变更日志（Step B 用，当前未接入 UI）=====

/**
 * 单字段变更详情（diffDtoList JSON 解析后的元素）
 */
export interface ParamLogDiff {
  name: string
  oldValue?: string
  newValue?: string
}

/**
 * 一条变更日志记录（后端真实字段）
 * data[] 直接是数组；总数在 res.count
 */
export interface ParamLogItem {
  id: string
  /** 操作类型：NEW 新增 / MODIFY 修改 / DELETE 删除（后端存在 msg 字段） */
  msg: 'NEW' | 'MODIFY' | 'DELETE' | string
  /** diff 列表的 JSON 字符串，需前端解析 */
  diffDtoList: string
  /** 操作人 */
  creator: string
  /** 操作时间 */
  createDate: string
}
